import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';

import { PECScanData } from '../../../../utils/models';
import { getPECScanReadingColumns } from '../../../../utils/data';

import AutTable from '../../../blocks/aut-grid-table';
import Conditional from '../../../common/conditional';
import ExcelImport from '../../../common/excelImport';
import PECHorizontalColumns from '../../../forms/pec/readings/pecHorizontalColumns';
import PECReadingForm from '../../../forms/pec/readings/pecReadingForm';
import PECReadingEditForm from '../../../forms/pec/readings/pecReadingEditForm';
import PECHorizontalColumnsEdit from '../../../forms/pec/readings/pecHorizontalColumnsEdit';
import Select from '../../../themed/select/select';

import * as toastService from '../../../../services/toastService';

type SortOrder = 'asc' | 'desc';

interface CustomPECScanItemReadingsProps {
    corrosionAllowance: number;
    nominalThickness: number;
    readings: PECScanData['readings'];
    useSwappedLabels: boolean;
    onReadingChange: (readings: PECScanData['readings']) => void;
}

const CustomPECScanItemReadings: React.FC<CustomPECScanItemReadingsProps> = ({ corrosionAllowance, nominalThickness, readings, useSwappedLabels, onReadingChange  }) => {
    const [readingToEdit, setReadingToEdit] = useState<{ srNo: string; } | null>(null);
    const [circValueToEdit, setCircValueToEdit] = useState<number | null>(null);
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

    const getCircValuesFromReadings = useCallback((data: PECScanData['readings']['data']) => {
        let circValues = data
            .flatMap((item) => Object.keys(item as object))
            .filter((circValue) => circValue !== 'srNo')
            .map((circValue) => parseFloat(circValue));

        circValues = _.orderBy(_.uniq(circValues), (item) => item, ['asc']);

        return circValues;
    }, []);

    const getTemplateColumns = useCallback((data: PECScanData['readings']['data']) => {
        const circValues = getCircValuesFromReadings(data);
        return getPECScanReadingColumns(nominalThickness, corrosionAllowance, circValues, useSwappedLabels);
    }, [getCircValuesFromReadings, corrosionAllowance, nominalThickness, useSwappedLabels]);

    const handleReadingAdd = (newItem: any) => {
		const readingMatch = readings.data.find((item) => item.srNo === newItem.srNo);
		if (readingMatch) return toastService.error('Item with the same Circ. Value exists!');

        handleSortReadings({ ...readings, data: [...readings.data, newItem] });
	};

	const handleReadingDelete = (itemToDelete: any): void => {
        const updatedItems = readings.data.filter((item) => item.srNo !== itemToDelete.srNo);
        onReadingChange({ ...readings, data: updatedItems });
	};
	
    const handleCircValueDelete = (circValue: number): void => {
        const updatedItems = readings.circValues.filter((value) => value !== circValue);
        onReadingChange({ ...readings, circValues: updatedItems });
	};

    const handleReadingEdit = (updatedItem: any) => {
		const updatedData = readings.data.map((item) => item.srNo === readingToEdit?.srNo ? updatedItem : item);
        handleSortReadings({ ...readings, data: updatedData });

        setReadingToEdit(null);
    };

	const handleAddReadingCircValue = (newCircValue: number) => {
        handleSortReadings({ ...readings, circValues: [ ...readings.circValues, newCircValue]});
	};
	
    const handleEditReadingCircValue = (newCircValue: number) => {
        const updatedCircValues = readings.circValues.map((circValue) => circValue === circValueToEdit ? newCircValue : circValue);
        handleSortReadings({ ...readings, circValues: updatedCircValues });
        setCircValueToEdit(null)
	};

    const handleSortOrderChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSortOrder(event.target.value as SortOrder);
    };

    const handleSortReadings = useCallback((readingsToSort: PECScanData['readings']) => {
        const circValues = _.orderBy(readingsToSort.circValues, (value) => value, [sortOrder]);
        const data =  _.orderBy(readingsToSort.data, (item) => parseInt(item.srNo), [sortOrder]);

        onReadingChange({ circValues, data });
    }, [sortOrder]);

    useEffect(() => {
        handleSortReadings(readings);
    }, [handleSortReadings]);

    const columns = useMemo(() => {
        return getPECScanReadingColumns(nominalThickness, corrosionAllowance, readings.circValues, useSwappedLabels);
    }, [corrosionAllowance, nominalThickness, readings, useSwappedLabels]);

     const excelTemplate = useMemo(() => {
            const compressedReadings = readings.circValues.reduce((accumulator: any, currentValue) => {
                accumulator[currentValue] = 0;
    
                return accumulator;
            }, {});
    
            const dataShape = {
                ...compressedReadings,
                srNo: 0
            };
    
            return [dataShape];
    
    }, [readings.circValues]);

    const circValueData = readings.circValues.map((value) => ({ value }));

	return ( 
        <>
            <div className="p-4 rounded bg-light bordered mb-4">
                {circValueToEdit ? (
                    <PECHorizontalColumnsEdit label={circValueToEdit} onEdit={handleEditReadingCircValue} onCancelEdit={() => setCircValueToEdit(null)} />
                ) : (
                    <PECHorizontalColumns onAdd={handleAddReadingCircValue} />
                )}
           
                <Conditional isVisible={readings.circValues.length > 0}>
                    <div className="divider my-4"></div>

                    <div className="p-4 rounded bg-white bordered mb-4">
                        <AutTable
                            tableColumns={[{ path: 'value', label: useSwappedLabels ? 'Circ. Value' : 'Axial Value' }]}
                            data={circValueData}
                            onEditItem={(item) => setCircValueToEdit(item.value)}
                            onDeleteItem={(item) => handleCircValueDelete(item.value)}
                        />
                    </div>

                    <div className="divider my-4"></div>

                    <ExcelImport
                        template={excelTemplate}
                        generateTemplateColumns={getTemplateColumns} 
                        onImportData={(data) => onReadingChange({ circValues: getCircValuesFromReadings(data), data })}
                    />
                </Conditional>
            </div>

            <div className="divider my-4"></div>

            <div className="p-4 rounded bg-light bordered mb-4">
                {readingToEdit ? 
                    <PECReadingEditForm 
                        columns={columns}
                        readingToEdit={readingToEdit} 
                        onCancelEdit={() => setReadingToEdit(null)} 
                        onEdit={handleReadingEdit}
                    /> : (
                    <PECReadingForm 
                        columns={columns} 
                        onAdd={handleReadingAdd} 
                    />
                )}
           
                <Conditional isVisible={readings.data.length > 0}>
                    <div className="divider my-4"></div>

                    <div className="d-flex justify-content-end">
                        <div className="p-2 pb-0 rounded bg-white bordered mb-4 ">
                            <Select 
                                name='sortOrder' 
                                placeholder='Select Sort Order'
                                value={sortOrder} 
                                options={SORT_ORDER}
                                onChange={handleSortOrderChange} 
                            />
                        </div>
                    </div>

                    <div className="p-4 rounded bg-white bordered mb-4">
                        <AutTable
                            tableColumns={columns}
                            data={readings.data}
                            onEditItem={(reading) => setReadingToEdit(reading)}
                            onDeleteItem={handleReadingDelete}
                        />
                    </div>
                </Conditional>
            </div>

        </>
     );
}

const SORT_ORDER = [
    {
        id: 'asc',
        name: 'Smallest to Largest',
    },
    {
        id: 'desc',
        name: 'Largest to Smallest',
    },
]
 
export default CustomPECScanItemReadings;
